import { EvaluationColor } from '@repo/types';
import { Typography } from '@repo/ui';
import { Card } from 'components';
import { useCallback, useMemo, useState } from 'react';

import ParticleSphere, {
  ResilienceLevel,
} from 'components/ParticleSphere/ParticleSphere';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { evaluationToLevel } from 'helpers/gutResilience';

import styles from './DisruptionModelSection.module.css';

type LevelEvaluation = Exclude<EvaluationColor, 'grey'>;

type SubCardCopy = {
  pill: string;
  pillEvaluation: LevelEvaluation;
  description: string;
};

type LevelContent = {
  gutStability: SubCardCopy;
  rateOfChange: SubCardCopy;
  vulnerability: SubCardCopy;
};

const LEVEL_CONTENT: Record<ResilienceLevel, LevelContent> = {
  low: {
    gutStability: {
      pill: 'Low',
      pillEvaluation: 'red',
      description:
        "This microbiome's balance, diversity, and core functions could shift frequently, without a consistent baseline to fall back on.",
    },
    rateOfChange: {
      pill: 'High',
      pillEvaluation: 'red',
      description:
        'This microbiome shifts quickly in response to inputs, meaning positive changes can take effect fast — but so can negative ones.',
    },
    vulnerability: {
      pill: 'High',
      pillEvaluation: 'red',
      description:
        'This microbiome is easily disrupted by even small changes, making consistency in diet, sleep, and stress especially important.',
    },
  },
  borderline: {
    gutStability: {
      pill: 'Low',
      pillEvaluation: 'red',
      description:
        'This microbiome has some balance and diversity, but fluctuates more than ideal, making it harder to consistently support core functions.',
    },
    rateOfChange: {
      pill: 'Moderate',
      pillEvaluation: 'orange',
      description:
        'This microbiome responds to changes at a normal pace, reflecting shifts in diet, sleep, and lifestyle over time.',
    },
    vulnerability: {
      pill: 'High',
      pillEvaluation: 'red',
      description:
        'This microbiome is mostly resilient but may show sensitivity to bigger disruptions like illness or significant diet changes.',
    },
  },
  typical: {
    gutStability: {
      pill: 'Moderate',
      pillEvaluation: 'orange',
      description:
        "This microbiome maintains a reasonably consistent balance over time, with normal fluctuations that don't significantly disrupt function.",
    },
    rateOfChange: {
      pill: 'Moderate',
      pillEvaluation: 'orange',
      description:
        'This microbiome responds to changes, but somewhat slowly — positive habits will take effect, just gradually.',
    },
    vulnerability: {
      pill: 'Low',
      pillEvaluation: 'green',
      description:
        'This microbiome has a normal level of resilience, managing everyday disruptions without significant issues.',
    },
  },
  high: {
    gutStability: {
      pill: 'High',
      pillEvaluation: 'green',
      description:
        'This microbiome stays highly consistent over time, maintaining a strong and reliable balance even as life changes around it.',
    },
    rateOfChange: {
      pill: 'Low',
      pillEvaluation: 'green',
      description:
        'This microbiome is slow to shift in response to changes, meaning new habits may take longer to have a noticeable impact.',
    },
    vulnerability: {
      pill: 'Low',
      pillEvaluation: 'green',
      description:
        'This microbiome handles disruptions well and is unlikely to be significantly thrown off by minor challenges.',
    },
  },
};

const LEVEL_ORDER: ResilienceLevel[] = ['low', 'borderline', 'typical', 'high'];

const LEVEL_LABEL: Record<ResilienceLevel, string> = {
  low: 'Low',
  borderline: 'Borderline',
  typical: 'Typical',
  high: 'High',
};

const COLOR_KEY: Array<{ label: string; swatchClass: string }> = [
  { label: 'Beneficial microbes', swatchClass: styles.swatchBeneficial },
  { label: 'Variable microbes', swatchClass: styles.swatchVariable },
  { label: 'Unfriendly microbes', swatchClass: styles.swatchUnfriendly },
  { label: 'Unknown', swatchClass: styles.swatchUnknown },
];

type DisruptionModelSectionProps = {
  userEvaluationColor: string | undefined | null;
};

const DisruptionModelSection = ({
  userEvaluationColor,
}: DisruptionModelSectionProps) => {
  const userLevel = useMemo(
    () => evaluationToLevel(userEvaluationColor),
    [userEvaluationColor],
  );
  const [selectedLevel, setSelectedLevel] =
    useState<ResilienceLevel>(userLevel);
  const { trackEvent } = useAnalytics();

  const handleLevelChange = useCallback(
    (newLevel: ResilienceLevel) => {
      if (newLevel === selectedLevel) return;
      setSelectedLevel(newLevel);
      trackEvent('GRS Interactive Card Tab Changed', {
        selected_state: newLevel,
        user_score_state: userLevel,
      });
    },
    [selectedLevel, userLevel, trackEvent],
  );

  return (
    <Card className={styles.card} variant='wrapper'>
      <div className={styles.cardInner}>
        <div className={styles.header}>
          <Typography variant='heading-m' className={styles.title}>
            What happens when your gut is disrupted
          </Typography>
          <Typography variant='body-s' className={styles.intro}>
            A resilient gut isn't just about which microbes you have — it's
            about how well they work together. A tight-knit, diverse community
            can absorb disruptions that would throw a weaker one off balance.
          </Typography>
          <Typography variant='body-s' className={styles.intro}>
            Interact with this model to see how disruptions impact your gut at
            different resilience levels.
          </Typography>
        </div>

        <div className={styles.controls}>
          <Typography
            variant='eyebrow'
            className={styles.segmentControlLabel}
            aria-hidden='true'
          >
            –&nbsp;&nbsp;Select resilience level&nbsp;&nbsp;–
          </Typography>
          <div
            role='radiogroup'
            aria-label='Select resilience level'
            className={styles.segmentControl}
          >
            {LEVEL_ORDER.map(lvl => {
              const isSelected = lvl === selectedLevel;
              return (
                <button
                  key={lvl}
                  type='button'
                  role='radio'
                  aria-checked={isSelected}
                  onClick={() => handleLevelChange(lvl)}
                  className={
                    isSelected
                      ? `${styles.segmentControlButton} ${styles.segmentControlButtonSelected}`
                      : styles.segmentControlButton
                  }
                >
                  {LEVEL_LABEL[lvl]}
                </button>
              );
            })}
          </div>

          <ul className={styles.colorKey} aria-label='Microbe categories'>
            {COLOR_KEY.map(({ label, swatchClass }) => (
              <li key={label} className={styles.colorKeyItem}>
                <span
                  className={`${styles.swatch} ${swatchClass}`}
                  aria-hidden='true'
                />
                <Typography variant='label' className={styles.colorKeyLabel}>
                  {label}
                </Typography>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.visualization}>
          <ParticleSphere level={selectedLevel} interactive showHint />
        </div>

        <div className={styles.subCardsStack}>
          {LEVEL_ORDER.map(lvl => (
            <div
              key={lvl}
              className={
                lvl === selectedLevel
                  ? styles.subCards
                  : `${styles.subCards} ${styles.subCardsHidden}`
              }
              aria-hidden={lvl !== selectedLevel}
            >
              <SubCard
                title='Gut stability'
                content={LEVEL_CONTENT[lvl].gutStability}
              />
              <SubCard
                title='Rate of change'
                content={LEVEL_CONTENT[lvl].rateOfChange}
              />
              <SubCard
                title='Vulnerability'
                content={LEVEL_CONTENT[lvl].vulnerability}
              />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

const SubCard = ({
  title,
  content,
}: {
  title: string;
  content: SubCardCopy;
}) => (
  <div className={styles.subCard}>
    <Typography variant='eyebrow' className={styles.subCardTitle}>
      {title}
    </Typography>
    <div
      className={`${styles.subCardPill} ${styles[`subCardPill_${content.pillEvaluation}`]}`}
    >
      <Typography variant='subheading'>{content.pill}</Typography>
    </div>
    <Typography variant='caption' className={styles.subCardDescription}>
      {content.description}
    </Typography>
  </div>
);

export default DisruptionModelSection;
